import { socialApi } from "@bw/shared/api";
import { invalidateFeedQueries } from "@bw/shared/hooks";
import type { BlockStatusResponse } from "@bw/shared/types/generated";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "../query/queryClient";
import { revokeBlockedPlayerConversations } from "./conversationCacheAuthority";

/** Shared token-scoped block truth for profile actions and recovery controls. */
export function useBlockStatus(userId: string, enabled = true) {
  return useQuery<BlockStatusResponse>({
    queryKey: queryKeys.social.blockStatus(userId),
    queryFn: () => socialApi.getBlockStatus(userId),
    enabled: enabled && Boolean(userId),
  });
}

/** Token-scoped block truth and its canonical path-based mutation. */
export function useBlockPlayer(userId: string, enabled = true) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.social.blockStatus(userId);
  const status = useBlockStatus(userId, enabled);
  const change = useMutation<void, unknown, boolean>({
    mutationFn: (nextBlocked) =>
      nextBlocked ? socialApi.block(userId) : socialApi.unblock(userId),
    onSuccess: async (_result, nextBlocked) => {
      queryClient.setQueryData<BlockStatusResponse>(queryKey, (current) => ({
        is_blocked: nextBlocked,
        is_blocked_by: current?.is_blocked_by ?? false,
      }));
      if (nextBlocked) {
        // Blocking ends message authority immediately. Message history uses a
        // privacy-isolated key family, so the broad Social invalidation below
        // cannot be relied on to evict cached conversation bodies.
        await revokeBlockedPlayerConversations(queryClient, userId);
      }
      // A block removes friendship/follow edges and hides conversations in
      // addition to changing the recovery list. Refresh the whole Social arm;
      // the status value above remains immediately server-confirmed while the
      // other affected views re-read canonical truth.
      void queryClient.invalidateQueries({ queryKey: queryKeys.social.all });
      invalidateFeedQueries(queryClient);
    },
  });

  return { status, change };
}
